import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BulkImportModal = ({ isOpen, onClose, onImport }) => {
  const [importMethod, setImportMethod] = useState('text');
  const [textData, setTextData] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkType, setLinkType] = useState('external'); // 'external' or 'internal'
  const [capturedImage, setCapturedImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  const sampleTextData = `Spaghetti Carbonara
Italian cuisine, serves 4, 25 minutes prep time
Ingredients: Spaghetti, Eggs, Pancetta, Parmesan cheese, Black pepper, Olive oil
Instructions:
1. Cook spaghetti in salted boiling water according to package directions until al dente.
2. While pasta cooks, fry pancetta in a large pan until crispy.
3. In a bowl, whisk together eggs, grated Parmesan, and black pepper.
4. Drain pasta, reserving 1 cup of pasta water.
5. Add hot pasta to the pan with pancetta.
6. Remove from heat and quickly stir in egg mixture, adding pasta water as needed.
7. Serve immediately with extra Parmesan and black pepper.
Notes: Traditional Roman recipe. Never add cream!

---

Chicken Tikka Masala
Indian cuisine, serves 6, 45 minutes prep time
Ingredients: Chicken breast, Yogurt, Tomatoes, Heavy cream, Garam masala, Turmeric, Garlic, Ginger, Onion
Dietary: Gluten-free
Instructions:
1. Cut chicken into bite-sized pieces and marinate in yogurt and spices for 30 minutes.
2. Cook marinated chicken in a hot pan until golden brown.
3. In the same pan, sauté onions, garlic, and ginger until fragrant.
4. Add tomatoes and spices, cook until thick.
5. Stir in cream and cooked chicken.
6. Simmer for 10-15 minutes until sauce thickens.
7. Serve with basmati rice and naan bread.
Notes: Popular British-Indian dish. Adjust spice level to taste.`;

  const parseTextData = (text) => {
    try {
      const recipes = text?.split('---')?.map(recipe => recipe?.trim())?.filter(recipe => recipe?.length > 0);
      const parsedDishes = [];

      for (const recipe of recipes) {
        const lines = recipe?.split('\n')?.map(line => line?.trim())?.filter(line => line?.length > 0);
        
        if (lines?.length < 3) {
          throw new Error('Each recipe must have at least a name, cuisine/serving info, and ingredients');
        }

        const dish = {
          name: '',
          cuisine: '',
          prepTime: 30,
          servings: 4,
          ingredients: [],
          dietaryRestrictions: [],
          instructions: '',
          notes: ''
        };

        // Parse recipe name (first line)
        dish.name = lines?.[0];

        // Parse second line (cuisine, servings, prep time)
        const infoLine = lines?.[1]?.toLowerCase();
        
        // Extract cuisine
        const cuisineMatch = infoLine?.match(/^([^,]+)/);
        if (cuisineMatch) {
          dish.cuisine = cuisineMatch?.[1]?.replace('cuisine', '')?.trim();
          dish.cuisine = dish?.cuisine?.charAt(0)?.toUpperCase() + dish?.cuisine?.slice(1);
        }

        // Extract servings
        const servingsMatch = infoLine?.match(/serves?\s+(\d+)/);
        if (servingsMatch) {
          dish.servings = parseInt(servingsMatch?.[1]);
        }

        // Extract prep time
        const prepTimeMatch = infoLine?.match(/(\d+)\s+minutes?\s+prep/);
        if (prepTimeMatch) {
          dish.prepTime = parseInt(prepTimeMatch?.[1]);
        }

        // Parse ingredients
        const ingredientsIndex = lines?.findIndex(line => line?.toLowerCase()?.startsWith('ingredients:'));
        if (ingredientsIndex !== -1 && ingredientsIndex + 1 < lines?.length) {
          const ingredientsLine = lines?.[ingredientsIndex]?.replace(/^ingredients:\s*/i, '');
          dish.ingredients = ingredientsLine?.split(',')?.map(ing => ing?.trim())?.filter(ing => ing?.length > 0);
        }

        // Parse dietary restrictions
        const dietaryIndex = lines?.findIndex(line => line?.toLowerCase()?.startsWith('dietary:'));
        if (dietaryIndex !== -1) {
          const dietaryLine = lines?.[dietaryIndex]?.replace(/^dietary:\s*/i, '');
          dish.dietaryRestrictions = dietaryLine?.split(',')?.map(diet => diet?.trim()?.toLowerCase())?.filter(diet => diet?.length > 0);
        }

        // Parse instructions
        const instructionsIndex = lines?.findIndex(line => line?.toLowerCase()?.startsWith('instructions:'));
        if (instructionsIndex !== -1) {
          const instructionLines = [];
          for (let i = instructionsIndex + 1; i < lines?.length; i++) {
            const line = lines?.[i];
            if (line?.toLowerCase()?.startsWith('notes:')) break;
            if (line?.match(/^\d+\./)) {
              instructionLines?.push(line);
            }
          }
          dish.instructions = instructionLines?.join('\n');
        }

        // Parse notes
        const notesIndex = lines?.findIndex(line => line?.toLowerCase()?.startsWith('notes:'));
        if (notesIndex !== -1) {
          dish.notes = lines?.[notesIndex]?.replace(/^notes:\s*/i, '');
        }

        if (!dish?.name || !dish?.cuisine || dish?.ingredients?.length === 0) {
          throw new Error(`Recipe "${dish.name || 'Unknown'}" is missing required fields`);
        }

        parsedDishes?.push(dish);
      }

      return { isValid: true, data: parsedDishes };
    } catch (error) {
      return { isValid: false, error: error?.message };
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices?.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera if available
      });
      setStream(mediaStream);
      if (videoRef?.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setShowCamera(true);
    } catch (error) {
      setErrors({ camera: 'Unable to access camera. Please check permissions.' });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream?.getTracks()?.forEach(track => track?.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef?.current && canvasRef?.current) {
      const video = videoRef?.current;
      const canvas = canvasRef?.current;
      const context = canvas?.getContext('2d');
      
      canvas.width = video?.videoWidth;
      canvas.height = video?.videoHeight;
      context?.drawImage(video, 0, 0);
      
      canvas?.toBlob((blob) => {
        const file = new File([blob], `recipe-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setCapturedImage(file);
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file && file?.type?.startsWith('image/')) {
      setCapturedImage(file);
    } else {
      setErrors({ image: 'Please select a valid image file.' });
    }
  };

  const handleImport = async () => {
    setIsProcessing(true);
    setErrors({});

    try {
      let dishesToImport = [];

      if (importMethod === 'text') {
        if (!textData?.trim()) {
          setErrors({ general: 'Please enter recipe text to import.' });
          setIsProcessing(false);
          return;
        }

        const validation = parseTextData(textData);
        if (!validation?.isValid) {
          setErrors({ general: validation?.error });
          setIsProcessing(false);
          return;
        }
        dishesToImport = validation?.data;
        
      } else if (importMethod === 'link') {
        if (!linkUrl?.trim()) {
          setErrors({ general: 'Please enter a valid URL.' });
          setIsProcessing(false);
          return;
        }

        // Create a placeholder dish with link information
        // In a real application, you might want to scrape the URL for recipe data
        dishesToImport = [{
          name: `Recipe from ${linkType === 'external' ? 'Web' : 'Internal'} Link`,
          cuisine: 'Various',
          prepTime: 30,
          servings: 4,
          ingredients: ['See link for ingredients'],
          dietaryRestrictions: [],
          instructions: `Recipe available at: ${linkUrl}`,
          notes: `Imported from ${linkType} link. Click the link to view full recipe details.`,
          sourceLink: linkUrl,
          linkType: linkType
        }];
        
      } else if (importMethod === 'camera') {
        if (!capturedImage) {
          setErrors({ general: 'Please capture or select an image.' });
          setIsProcessing(false);
          return;
        }

        // Create a placeholder dish with image
        // In a real application, you might want to use OCR to extract recipe data from the image
        const imageUrl = URL.createObjectURL(capturedImage);
        dishesToImport = [{
          name: 'Recipe from Photo',
          cuisine: 'Various',
          prepTime: 30,
          servings: 4,
          ingredients: ['See photo for ingredients'],
          dietaryRestrictions: [],
          instructions: 'Recipe details captured in photo. Please edit to add specific instructions.',
          notes: 'Imported from photo. Please review and update recipe details.',
          image: imageUrl,
          imageAlt: 'Recipe captured from photo'
        }];
      }

      // Process and enhance the data
      const processedDishes = dishesToImport?.map(dish => ({
        ...dish,
        id: Date.now() + Math.random(),
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString(),
        usageCount: dish?.usageCount || 0,
        rating: dish?.rating || null,
        lastUsed: dish?.lastUsed || null,
        isNew: true
      }));

      await onImport(processedDishes);
      
      // Reset form
      setTextData('');
      setLinkUrl('');
      setCapturedImage(null);
      setImportMethod('text');
      
      onClose();
    } catch (error) {
      setErrors({ general: 'Failed to import dishes. Please try again.' });
    }

    setIsProcessing(false);
  };

  const handleReset = () => {
    setTextData('');
    setLinkUrl('');
    setCapturedImage(null);
    setErrors({});
    stopCamera();
  };

  const hasData = () => {
    switch (importMethod) {
      case 'text':
        return textData?.trim()?.length > 0;
      case 'link':
        return linkUrl?.trim()?.length > 0;
      case 'camera':
        return capturedImage !== null;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-background/80 backdrop-blur-sm" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-card shadow-warm-lg rounded-lg">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">Import Recipes</h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          <div className="p-6 space-y-6">
            {/* Import Method Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">Import Method</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center space-x-3 cursor-pointer p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                  <input
                    type="radio"
                    name="importMethod"
                    value="text"
                    checked={importMethod === 'text'}
                    onChange={(e) => setImportMethod(e?.target?.value)}
                    className="text-primary focus:ring-primary"
                  />
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={16} />
                    <span className="text-sm font-medium text-foreground">Text</span>
                  </div>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                  <input
                    type="radio"
                    name="importMethod"
                    value="link"
                    checked={importMethod === 'link'}
                    onChange={(e) => setImportMethod(e?.target?.value)}
                    className="text-primary focus:ring-primary"
                  />
                  <div className="flex items-center space-x-2">
                    <Icon name="Link" size={16} />
                    <span className="text-sm font-medium text-foreground">Link</span>
                  </div>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                  <input
                    type="radio"
                    name="importMethod"
                    value="camera"
                    checked={importMethod === 'camera'}
                    onChange={(e) => setImportMethod(e?.target?.value)}
                    className="text-primary focus:ring-primary"
                  />
                  <div className="flex items-center space-x-2">
                    <Icon name="Camera" size={16} />
                    <span className="text-sm font-medium text-foreground">Photo</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Text Import */}
            {importMethod === 'text' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">Recipe Text</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTextData(sampleTextData)}
                  >
                    <Icon name="FileText" size={14} className="mr-1" />
                    Load Sample
                  </Button>
                </div>
                <textarea
                  value={textData}
                  onChange={(e) => setTextData(e?.target?.value)}
                  placeholder="Paste your recipe text here. Separate multiple recipes with '---'"
                  rows={12}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-vertical"
                />
                <div className="mt-2 text-xs text-muted-foreground">
                  Tip: Use "---" to separate multiple recipes. Include recipe name, cuisine info, ingredients, and instructions.
                </div>
              </div>
            )}

            {/* Link Import */}
            {importMethod === 'link' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Link Type</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="linkType"
                        value="external"
                        checked={linkType === 'external'}
                        onChange={(e) => setLinkType(e?.target?.value)}
                        className="text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">External (www)</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="linkType"
                        value="internal"
                        checked={linkType === 'internal'}
                        onChange={(e) => setLinkType(e?.target?.value)}
                        className="text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">Internal</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Recipe URL</label>
                  <Input
                    type="url"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e?.target?.value)}
                    placeholder={linkType === 'external' ? 'https://www.example.com/recipe' : '/internal/recipe/path'}
                  />
                </div>
              </div>
            )}

            {/* Camera Import */}
            {importMethod === 'camera' && (
              <div className="space-y-4">
                {!showCamera && !capturedImage && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={startCamera} className="flex-1">
                      <Icon name="Camera" size={16} className="mr-2" />
                      Take Photo
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef?.current?.click()}
                      className="flex-1"
                    >
                      <Icon name="Upload" size={16} className="mr-2" />
                      Choose Photo
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                )}

                {showCamera && (
                  <div className="space-y-3">
                    <div className="relative bg-black rounded-lg overflow-hidden">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    <div className="flex justify-center space-x-3">
                      <Button onClick={capturePhoto}>
                        <Icon name="Camera" size={16} className="mr-2" />
                        Capture
                      </Button>
                      <Button variant="outline" onClick={stopCamera}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {capturedImage && (
                  <div className="space-y-3">
                    <div className="relative bg-muted rounded-lg overflow-hidden">
                      <img
                        src={URL.createObjectURL(capturedImage)}
                        alt="Captured recipe"
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    <div className="flex justify-center space-x-3">
                      <Button variant="outline" onClick={() => setCapturedImage(null)}>
                        <Icon name="X" size={16} className="mr-2" />
                        Remove
                      </Button>
                      <Button onClick={startCamera}>
                        <Icon name="Camera" size={16} className="mr-2" />
                        Take Another
                      </Button>
                    </div>
                  </div>
                )}

                <canvas ref={canvasRef} className="hidden" />
              </div>
            )}

            {/* Format Instructions */}
            <div className="bg-muted rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-2">Import Guidelines:</h4>
              {importMethod === 'text' ? (
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Each recipe should start with the recipe name</li>
                  <li>• Include cuisine, servings, and prep time on the second line</li>
                  <li>• List ingredients after "Ingredients:" label</li>
                  <li>• Add step-by-step instructions after "Instructions:" label</li>
                  <li>• Separate multiple recipes with "---"</li>
                </ul>
              ) : importMethod === 'link' ? (
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• External links should start with https:// or http://</li>
                  <li>• Internal links can be relative paths within your app</li>
                  <li>• Link will be saved as recipe source for reference</li>
                  <li>• You can edit the imported recipe details afterward</li>
                </ul>
              ) : (
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Take a clear photo of the recipe or ingredient list</li>
                  <li>• Ensure good lighting for best quality</li>
                  <li>• You can edit and add details after importing</li>
                  <li>• Supported formats: JPG, PNG, WEBP</li>
                </ul>
              )}
            </div>

            {/* Errors */}
            {(errors?.general || errors?.camera || errors?.image) && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-error" />
                  <p className="text-sm text-error font-medium">Import Error</p>
                </div>
                <p className="text-sm text-error mt-1">
                  {errors?.general || errors?.camera || errors?.image}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between p-6 border-t border-border">
            <Button variant="outline" onClick={handleReset}>
              <Icon name="RotateCcw" size={16} className="mr-2" />
              Reset
            </Button>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={isProcessing || !hasData()}
                loading={isProcessing}
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Import Recipe{importMethod === 'text' && textData?.includes('---') ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;